import type { FileError, FileRejection } from 'react-dropzone';

/**
 * Module augment this interface to add arbitrary metadata to the {@link FileLike}
 * type used to define files in the {@link FileUploader} component.
 */
// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface FileUploaderFileMetaOverrides {}

/**
 * A metadata object shape that resembles a file system file,
 * but is not actually a fully-formed `File` object.
 *
 * @see FileLike
 */
// Use an interface, as opposed to an intersection w/ `Pick`, to improve Dart generation.
interface FileMeta extends FileUploaderFileMetaOverrides {
  /** [MDN Reference](https://developer.mozilla.org/docs/Web/API/Blob/size) */
  readonly name: File['name'];
  /** [MDN Reference](https://developer.mozilla.org/docs/Web/API/File/name) */
  readonly size: File['size'];
  /** [MDN Reference](https://developer.mozilla.org/docs/Web/API/Blob/type) */
  readonly type: File['type'];
}

/**
 * The object that represents a file that is displayed by the
 * {@link FileUploader} component.
 *
 * This can be either a fully-formed file system `File`, or an object
 * that contains the name, size and type metadata values from a previously
 * uploaded file.
 *
 * Custom metadata fields are also supported through the augmentation of the
 * {@link FileUploaderFileMetaOverrides} interface.
 */
export type FileLike = File | FileMeta;

/**
 * The model that is used to control a `FileUploader` component's display of `FileListItem`s.
 */
export type FileModel = {
  /**
   * The object that represents the file that was uploaded by the end user.
   *
   * This can be either a fully-formed file system `File`, or an object
   * that contains the name, size and type metadata values from a
   * previously uploaded file.
   *
   * Available from `FileUploaderProps.onChange`.
   */
  file: FileLike;
  /**
   * An optional list of errors associated with the uploaded file.
   *
   * Available from `FileUploaderProps.onChange.fileRejections.errors`.
   *
   * ### Example
   * ```ts
   * import * as React from 'react';
   * import { FileUploader, type FileUploaderProps } from '@workiva/unify';
   *
   * function ControlledFileUploaderWithCustomErrors(
   *   props: Omit<FileUploaderProps, 'fileValues'> & {
   *     initialFileValues?: FileUploaderProps['fileValues'];
   *   }
   * ): React.ReactElement {
   *   const {
   *     initialFileValues,
   *     onChange: onChangeProp,
   *     setFileValues: setFileValuesProp,
   *     ...rest
   *   } = props;
   *   const [fileValues, setFileValues] = React.useState(initialFileValues);
   *   const handleChange = React.useCallback<FileUploaderProps['onChange']>(
   *     (fileModelsIn, alertErrors) => {
   *       const fileModels = fileModelsIn.map((fileModelIn) => {
   *         const fileModel = fileModelIn;
   *         if (fileModel.errors?.length) {
   *           fileModel.errors = fileModel.errors.map((error) => ({
   *             ...error,
   *             // Use the `error` param to parse the
   *             // error code / existing message to
   *             // build custom error messages
   *             message: yourOwnErrorMessageLogic(error),
   *           }));
   *         }
   *
   *         return fileModel;
   *       });
   *
   *       setFileValues(fileModels);
   *       onChangeProp?.(fileModels, alertErrors);
   *     },
   *     [onChangeProp]
   *   );
   *   return (
   *     <FileUploader fileValues={fileValues} onChange={handleChange} {...rest} />
   *   );
   * }
   */
  errors?: FileError[];
  /**
   * An optional number that represents the upload progress of the {file}.
   *
   * It is up to you to keep this value up-to-date based on the upload progress from your business logic.
   *
   * * Values between 0-100 represent determinate progress.
   *   Your business logic should continually update this value until it reaches 100.
   * * A value of -1 represents indeterminate progress.
   *   Your business logic should set this to 100 when the upload is complete.
   *
   * Setting this value will result in the rendered `FileListItem` displaying with a `CircularProgress` component to visually communicate progress to the end user.
   *
   * ### Example
   * ```ts
   * import * as React from 'react';
   * import { FileUploader, type FileUploaderProps } from '@workiva/unify';
   *
   * // In this example the assumption is that the `filesUploaded` function is a store
   * // action handler of some kind that is called from within the `onChange` handler
   * // of your `FileUploader` consumption. The action handler should update your
   * // application state with `FileModel`s that have `uploadPercentComplete` from
   * // whatever service / endpoing is providing that data.
   * //
   * // THIS IS NOT WORKING CODE FOR ANY PARTICULAR STATE MACHINE LIBRARY.
   * // IT IS MEANT TO BE PSEUDO-CODE ONLY.
   * export function filesUploaded(fileModelsFromComponentOnChange: FileModel[]): FileModel[] {
   *   store.fileModels = fileModelsFromComponentOnChange.map((fileModel) => ({
   *     ...fileModel,
   *     uploadPercentComplete:
   *         // See documentation above for valid return values
   *         someServiceThatHandlesAsyncFileUploadTasks.getUploadProgress(fileModel.file);
   *   }));
   * }
   */
  uploadPercentComplete?: number;
};

/**
 * @access package
 */
export function buildDefaultFileModelFromFileOrFileRejection<
  T extends FileLike | FileRejection
>(fileOrFileRejection: T): FileModel {
  if ('errors' in fileOrFileRejection) {
    return {
      file: fileOrFileRejection.file,
      errors: [...fileOrFileRejection.errors],
    };
  }

  return { file: fileOrFileRejection };
}

export const defaultFileUploadPercentComplete = 100;
const fileUploadStatuses = ['done', 'uploading', 'error'] as const;
export type UploadStatus = (typeof fileUploadStatuses)[number];

/**
 * @access package
 */
export function fileHasErrors(modelErrors?: FileModel['errors']): boolean {
  if (!modelErrors) return false;
  return modelErrors.length > 0;
}

/**
 * @access package
 */
export function fileIsUploading(uploadPercentComplete?: number): boolean {
  return (
    typeof uploadPercentComplete !== 'undefined' && uploadPercentComplete < 100
  );
}

/**
 * @access package
 */
export function fileUploadIsIndeterminate(
  uploadPercentComplete: number
): boolean {
  return uploadPercentComplete === -1;
}

/**
 * A utility function that returns a string `fileStatus` value that
 * can be used to set the analogous prop on the `FileListItemIcon` component
 * when using the `FileUploader` component's `renderFileListItem` prop.
 *
 * @see The example for `renderFileListItem` within {@link FileUploaderProps}
 *
 * @access public
 */
export function getFileStatusFromModel(fileModel: FileModel): UploadStatus {
  // An error must have a non-undefined message to cause a file to display in an error state.
  // See our default error messaging functions in `fileUploaderDefaultErrorMessages` for more
  // info on how an error would come to have an undefined message.
  const errorsThatShouldBeShownAtFileItemLevel = fileModel.errors?.filter(
    (err) => err.message !== undefined
  );
  if (fileHasErrors(errorsThatShouldBeShownAtFileItemLevel)) {
    return 'error';
  } else if (fileIsUploading(fileModel.uploadPercentComplete)) {
    return 'uploading';
  } else {
    return 'done';
  }
}
